import { Skeleton } from "@xola/ui-kit";
import { GetDataPath, GetRowIdParams, GridApi, GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { sumBy } from "lodash";
import pluralize from "pluralize";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ExperienceItemStatus, Timeslot, WaitlistItem } from "types";
import { useAutoFetchCollection } from "../../../../hooks/useAutoFetchCollection";
import { Loader } from "../../../Loader";
import { columnDefs, popoverWaitlistParentId } from "./helpers";
import "./RosterWaitlistList.css";
import { getDemographicLineItems, getEquipmentRosterLineItems } from "@/models/PurchaseItem";

export const getWaitlistRowData = (waitlists: WaitlistItem[], timeslot: Timeslot) => {
    return waitlists.map((waitlist) => {
        const equipmentLineItems = getEquipmentRosterLineItems(waitlist);
        const countLineItems = [...getDemographicLineItems(waitlist), ...equipmentLineItems];
        const itemDemographicsFormatted: { [key: string]: number } = {};

        for (const lineItem of countLineItems) {
            if ((lineItem.quantity ?? 0) > 0) {
                itemDemographicsFormatted[lineItem.name] = lineItem.quantity ?? 0;
            }
        }

        return {
            id: waitlist.id,
            waitlist,
            timeslot,
            isEquipment: equipmentLineItems.length > 0,
            demographics: {
                value: itemDemographicsFormatted,
                length: Object.keys(itemDemographicsFormatted).length,
            },
        };
    });
};

interface DashboardRosterWaitlistProps {
    timeslot: Timeslot;
}

export const DashboardRosterWaitlist = ({ timeslot }: DashboardRosterWaitlistProps) => {
    const gridApi = useRef<GridApi | null>(null);

    const [waitlists, setWaitlists] = useState<WaitlistItem[]>([]);

    const {
        data: waitlistsForArrivalTimeRange,
        isLoading: isLoadingArrivalTimeRangeWaitlists,
        isFetching: isFetchingArrivalTimeRangeWaitlists,
    } = useAutoFetchCollection<WaitlistItem>(
        "purchaseItems",
        {
            product: timeslot.product,
            arrivalDate: timeslot.date,
            status: ExperienceItemStatus.WAITLIST_CREATED,
            expand: "conversation",
            "arrivalTimeRange.startTime[lte]": timeslot.time,
            "arrivalTimeRange.endTime[gte]": timeslot.time,
            "arrivalTime[nin]": timeslot.time,
        },
        { enabled: !!timeslot.time, staleTime: 60 * 1000 },
    );

    const {
        data: waitlistsForArrival,
        isLoading: isLoadingArrivalWaitlists,
        isFetching: isFetchingArrivalWaitlists,
    } = useAutoFetchCollection<WaitlistItem>(
        "purchaseItems",
        {
            product: timeslot.product,
            arrivalDate: timeslot.date,
            status: ExperienceItemStatus.WAITLIST_CREATED,
            expand: "conversation",
            arrivalTime: timeslot.time,
        },
        { staleTime: 60 * 1000 },
    );

    const rowData = useMemo(() => getWaitlistRowData(waitlists, timeslot), [waitlists, timeslot]);

    useEffect(() => {
        const combinedWaitlists: WaitlistItem[] = [];

        // Combine data from waitlistsForArrivalTime
        if (waitlistsForArrival) {
            combinedWaitlists.push(...waitlistsForArrival);
        }

        // Combine data from waitlistsForArrivalRange
        if (waitlistsForArrivalTimeRange) {
            combinedWaitlists.push(...waitlistsForArrivalTimeRange);
        }

        setWaitlists(combinedWaitlists);
    }, [waitlistsForArrival, waitlistsForArrivalTimeRange]);

    if (isLoadingArrivalTimeRangeWaitlists || isLoadingArrivalWaitlists) {
        return <Skeleton height={100} />;
    }

    const isFetchingNextPage = isFetchingArrivalTimeRangeWaitlists || isFetchingArrivalWaitlists;

    if (waitlists.length === 0) {
        return null;
    }

    const guestCount = sumBy(waitlists, (item) => item.quantity);
    const isEquipment = waitlists.some((item) => getEquipmentRosterLineItems(item).length > 0);
    const countLabel = isEquipment ? "Equipment" : pluralize("Guest", guestCount);

    const onGridReady = (params: GridReadyEvent) => {
        gridApi.current = params.api;
    };

    return (
        <div className="py-14">
            <h2 className="mb-4" data-testid="dashboard-roster-waitlist-groups-guests">
                Waitlisted Guests (<var data-var="waitlistsCount">{waitlists.length}</var>
                &nbsp;{pluralize("Group", waitlists.length)}
                <span className="mx-2 text-gray">&middot;</span>
                <var data-var="guestCount">{guestCount}</var> {countLabel})
            </h2>

            <div className="ag-theme-alpine ui-roster-waitlist-list" id={popoverWaitlistParentId}>
                <AgGridReact
                    suppressCellFocus
                    suppressContextMenu
                    suppressRowVirtualisation
                    suppressColumnVirtualisation
                    enableCellTextSelection
                    ensureDomOrder
                    theme="legacy"
                    rowData={rowData}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    getDataPath={getDataPath}
                    domLayout="autoHeight"
                    onGridReady={onGridReady}
                />
            </div>

            <div className="mt-4 text-center">{isFetchingNextPage ? <Loader /> : null}</div>
        </div>
    );
};

const getDataPath: GetDataPath = (data: any) => data.waitlist.id;

const getRowId = (params: GetRowIdParams) => {
    const { data } = params;

    return data.id;
};
